import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["writeTab", "previewTab", "writeContent", "previewContent", "bodyElements", "preview", "templateSelect", "customerPreviewButton", "customerPreviewLabel"];
  static values = {
    logoUrl: String,
    orgName: String,
    socialMedia: Object,
    website: String,
    location: String,
    country: String,
    templateBody: Array,
    buttonBgColor: String,
    buttonTextColor: String,
    templates: Array,
    previewUrl: String,
    previewLanguage: String,
    sourceLanguage: String
  };

  connect() {
    // Initialize immediately
    this.initialize();

    // Also listen for Turbo Frame load events in case the modal is loaded dynamically
    this.boundInitialize = () => this.initialize();
    document.addEventListener("turbo:frame-load", this.boundInitialize);

    // The inbox composer picks the recipient after render, so the customer's
    // language arrives via an event from the recipient picker.
    this.boundRecipientSelected = (event) => {
      this.previewLanguageValue = event.detail?.language || "";
      this.exitCustomerPreview();
      this.updateCustomerPreviewButton();
    };
    this.element.addEventListener("inbox-email-compose:recipientSelected", this.boundRecipientSelected);

    // Editing invalidates a customer-language preview.
    this.boundContentEdited = () => {
      if (!this.previewingCustomerLanguage) return;

      this.exitCustomerPreview();
      this.updatePreview();
    };
    this.element.addEventListener("input", this.boundContentEdited);

    this.updateCustomerPreviewButton();
  }

  disconnect() {
    // Clean up event listener
    if (this.boundInitialize) {
      document.removeEventListener("turbo:frame-load", this.boundInitialize);
    }
    if (this.boundRecipientSelected) {
      this.element.removeEventListener("inbox-email-compose:recipientSelected", this.boundRecipientSelected);
    }
    if (this.boundContentEdited) {
      this.element.removeEventListener("input", this.boundContentEdited);
    }
  }

  initialize() {
    // Clear any existing elements first (in case of form re-render with errors)
    if (this.hasBodyElementsTarget) {
      this.bodyElementsTarget.innerHTML = "";
    }
    
    // Initialize element index
    this.elementIndex = 0;
    
    // Load template body elements if any
    if (this.templateBodyValue && Array.isArray(this.templateBodyValue) && this.templateBodyValue.length > 0) {
      this.templateBodyValue.forEach((element, index) => {
        if (element && element.type) {
          this.addElementFromTemplate(element, index);
        }
      });
      this.elementIndex = this.templateBodyValue.length;
    } else {
      this.addElement("paragraph");
    }
    this.updatePreview();
  }

  showWrite(event) {
    event.preventDefault();
    this.activateTab(this.writeTabTarget, this.previewTabTarget);
    this.writeContentTarget.classList.remove("hidden");
    this.previewContentTarget.classList.add("hidden");
  }

  showPreview(event) {
    event.preventDefault();
    this.updatePreview();
    this.activateTab(this.previewTabTarget, this.writeTabTarget);
    this.writeContentTarget.classList.add("hidden");
    this.previewContentTarget.classList.remove("hidden");
  }

  activateTab(activeTab, inactiveTab) {
    activeTab.classList.add("ui-tab-item-active");
    inactiveTab.classList.remove("ui-tab-item-active");
  }

  addParagraph(event) {
    this.addElement("paragraph", event);
  }

  addButton(event) {
    this.addElement("button", event);
  }

  addLink(event) {
    this.addElement("link", event);
  }

  addImage(event) {
    this.addElement("image", event);
  }

  applyTemplate(event) {
    const selected = event.currentTarget.value;
    if (selected === "") return;

    const template = this.readTemplates()[Number(selected)];
    if (!template) return;

    this.exitCustomerPreview();

    const subject = this.element.querySelector("input[name='subject']");
    if (subject) subject.value = template.subject || "";

    this.bodyElementsTarget.innerHTML = "";
    this.elementIndex = 0;
    this.addElementFromTemplate({ type: "paragraph", content: template.body }, this.elementIndex++);
    this.updatePreview();
  }

  // Read the templates straight off the element so a value the browser could
  // not parse can never leave the picker silently doing nothing.
  readTemplates() {
    if (Array.isArray(this.templatesValue)) return this.templatesValue;

    const raw = this.element.dataset.emailComposerTemplatesValue;
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error("Could not read the email templates:", error);
      return [];
    }
  }

  addElement(type, event) {
    if (event) event.preventDefault();
    this.exitCustomerPreview();
    const template = document.getElementById(`${type}-email-element-template`);
    if (!template) {
      console.error(`Template not found: ${type}-email-element-template`);
      return;
    }

    const index = this.elementIndex++;
    const html = template.innerHTML.replace(/\{\{INDEX\}\}/g, index);
    
    const div = document.createElement("div");
    div.innerHTML = html;
    const element = div.firstElementChild;
    
    if (!element) {
      console.error(`Failed to create element from template: ${type}`);
      return;
    }
    
    this.bodyElementsTarget.appendChild(element);
    this.updatePreview();
  }

  addElementFromTemplate(element, index) {
    const type = element.type || element["type"];
    const template = document.getElementById(`${type}-email-element-template`);
    if (!template) return;

    const html = template.innerHTML.replace(/\{\{INDEX\}\}/g, index);

    const div = document.createElement("div");
    div.innerHTML = html;
    const elementDiv = div.firstElementChild;
    if (!elementDiv) return;

    this.fillElementFields(elementDiv, type, element);

    this.bodyElementsTarget.appendChild(elementDiv);
  }

  // The server renders the element templates as empty scaffolding, so write the
  // template's copy into the fields directly rather than relying on placeholder
  // strings being present in the markup.
  fillElementFields(elementDiv, type, values) {
    const setField = (field, value) => {
      const input = elementDiv.querySelector(`[name$="[${field}]"]`);
      if (input) input.value = value ?? "";
    };

    if (type === "paragraph") {
      setField("content", values.content ?? values["content"] ?? "");
    } else if (type === "button" || type === "link") {
      setField("text", values.text ?? values["text"] ?? "");
      setField("url", values.url ?? values["url"] ?? "");
    } else if (type === "image") {
      setField("url", values.url ?? values["url"] ?? "");
      setField("alt", values.alt ?? values["alt"] ?? "");
    }
  }

  removeElement(event) {
    const index = event.currentTarget.dataset.index;
    const element = this.bodyElementsTarget.querySelector(`[data-element-index="${index}"]`);
    if (element) {
      element.remove();
      this.exitCustomerPreview();
      this.updatePreview();
    }
  }

  async toggleCustomerPreview(event) {
    event.preventDefault();

    if (this.previewingCustomerLanguage) {
      this.exitCustomerPreview();
      this.updatePreview();
      return;
    }

    const language = this.previewLanguageValue;
    if (!this.previewUrlValue || !language) return;

    this.setCustomerPreviewLoading(true);

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
        },
        body: JSON.stringify({
          subject: this.element.querySelector("input[name='subject']")?.value || "",
          body_elements: this.collectBodyElements(),
          target_language: language
        })
      });

      if (!response.ok) throw new Error(`Preview failed (${response.status})`);

      const preview = await response.json();
      this.translatedPreview = preview;
      this.previewingCustomerLanguage = true;
      this.showPreviewTab();
      this.renderPreview(preview.subject || "", preview.body_elements || []);
    } catch (error) {
      console.error("Customer-language preview failed:", error);
      this.translatedPreview = null;
      this.previewingCustomerLanguage = false;
    } finally {
      this.setCustomerPreviewLoading(false);
      this.updateCustomerPreviewButton();
    }
  }

  exitCustomerPreview() {
    this.previewingCustomerLanguage = false;
    this.translatedPreview = null;
    this.updateCustomerPreviewButton();
  }

  setCustomerPreviewLoading(isLoading) {
    if (this.hasCustomerPreviewButtonTarget) {
      this.customerPreviewButtonTarget.disabled = isLoading;
    }
  }

  updateCustomerPreviewButton() {
    if (!this.hasCustomerPreviewButtonTarget) return;

    const language = this.previewLanguageValue;
    const isRelevant = !!language && language !== this.sourceLanguageValue;
    this.customerPreviewButtonTarget.classList.toggle("hidden", !isRelevant);

    if (!isRelevant || !this.hasCustomerPreviewLabelTarget) return;

    const template = this.previewingCustomerLanguage
      ? this.customerPreviewButtonTarget.dataset.activeLabelTemplate
      : this.customerPreviewButtonTarget.dataset.labelTemplate;

    this.customerPreviewLabelTarget.textContent = (template || "{{language}}").replace("{{language}}", language.toUpperCase());
    this.customerPreviewButtonTarget.classList.toggle("ui-tab-item-active", this.previewingCustomerLanguage);
  }

  showPreviewTab() {
    if (!this.hasPreviewTabTarget) return;

    this.activateTab(this.previewTabTarget, this.writeTabTarget);
    this.writeContentTarget.classList.add("hidden");
    this.previewContentTarget.classList.remove("hidden");
  }

  updatePreview() {
    if (!this.hasPreviewTarget) return;

    if (this.previewingCustomerLanguage && this.translatedPreview) {
      this.renderPreview(this.translatedPreview.subject || "", this.translatedPreview.body_elements || []);
      return;
    }

    // Get subject from form
    const subject = document.querySelector("input[name=\"subject\"]")?.value || "";

    this.renderPreview(subject, this.collectBodyElements());
  }

  collectBodyElements() {
    const bodyElements = [];
    this.bodyElementsTarget.querySelectorAll("[data-element-type]").forEach(elementDiv => {
      const type = elementDiv.dataset.elementType;
      const data = { type: type };
      elementDiv.querySelectorAll("input, textarea, select").forEach(input => {
        if (input.type === "hidden") return;
        const nameMatch = input.name.match(/\[([^\]]+)\]$/);
        if (nameMatch) {
          const name = nameMatch[1];
          data[name] = input.value;
        }
      });
      bodyElements.push(data);
    });

    return bodyElements;
  }

  renderPreview(subject, bodyElements) {
    let html = "<div class=\"email-preview-shell\">";

    // Header with logo
    html += "<div class=\"email-preview-panel email-preview-header email-preview-header--left\">";
    if (this.logoUrlValue && this.logoUrlValue.trim() !== "") {
      html += `<img src="${this.escapeHtml(this.logoUrlValue)}" alt="${this.escapeHtml(this.orgNameValue || "Logo")}" class="email-preview-logo" />`;
    } else {
      html += "<div class=\"email-preview-logo-placeholder\">[Your Logo]</div>";
    }
    html += "</div>";

    // Body
    html += "<div class=\"email-preview-panel\">";

    // Render body elements
    bodyElements.forEach(element => {
      switch(element.type) {
      case "paragraph": {
        const content = element.content || "";
        let formattedContent = content
          .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
          .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>")
          .replace(/\n/g, "<br>");
        formattedContent = this.escapeHtmlButPreserveTags(formattedContent);
        html += `<div class="email-preview-content email-preview-content--spaced">${formattedContent}</div>`;
        break;
      }
      case "button":
        if (element.url) {
          html += `<div class="email-preview-block email-preview-header--left"><a href="${this.escapeHtml(element.url)}" class="email-preview-button">${this.escapeHtml(element.text || "Click Here")}</a></div>`;
        }
        break;
      case "link":
        if (element.url) {
          html += `<div class="email-preview-block"><a href="${this.escapeHtml(element.url)}" class="email-preview-link">${this.escapeHtml(element.text || "Link Text")}</a></div>`;
        }
        break;
      case "image":
        if (element.url) {
          html += `<div class="email-preview-block email-preview-header--left"><img src="${this.escapeHtml(element.url)}" alt="${this.escapeHtml(element.alt || "")}" class="email-preview-image" /></div>`;
        }
        break;
      }
    });

    html += "</div>";

    // Footer
    html += "<div class=\"email-preview-footer\">";

    if (this.orgNameValue) {
      html += `<div class="email-preview-company">${this.escapeHtml(this.orgNameValue)}</div>`;
    }

    const contactInfo = [];
    if (this.locationValue) contactInfo.push(this.escapeHtml(this.locationValue));
    if (this.countryValue) contactInfo.push(this.escapeHtml(this.countryValue));
    if (this.websiteValue) {
      const websiteUrl = this.websiteValue.startsWith("http://") || this.websiteValue.startsWith("https://") ? this.websiteValue : `https://${this.websiteValue}`;
      contactInfo.push(`<a href="${this.escapeHtml(websiteUrl)}" class="email-preview-link email-preview-link--plain">${this.escapeHtml(this.websiteValue)}</a>`);
    }

    if (contactInfo.length > 0) {
      html += `<div class="email-preview-contact">${contactInfo.join(" | ")}</div>`;
    }

    // Social media
    const socialMedia = Object.entries(this.socialMediaValue || {}).filter(([_, url]) => url && url.trim() !== "");
    if (socialMedia.length > 0) {
      html += "<div class=\"email-preview-social\">";
      socialMedia.forEach(([platformName, url]) => {
        const displayName = platformName.charAt(0).toUpperCase() + platformName.slice(1);
        html += `<a href="${this.escapeHtml(url)}" class="email-preview-link email-preview-link--plain email-preview-social-link">${this.escapeHtml(displayName)}</a>`;
      });
      html += "</div>";
    }

    html += "</div>";
    html += "</div>";

    this.previewTarget.innerHTML = html;
  }

  escapeHtml(unsafe) {
    const div = document.createElement("div");
    div.textContent = unsafe;
    return div.innerHTML;
  }

  escapeHtmlButPreserveTags(text) {
    const placeholders = {};
    let placeholderIndex = 0;
    let safe = text.replace(/<(strong|em|br)\/?>|<\/(strong|em)>/gi, (match) => {
      const key = `__PLACEHOLDER_${placeholderIndex}__`;
      placeholders[key] = match;
      placeholderIndex++;
      return key;
    });
    
    safe = this.escapeHtml(safe);
    
    Object.keys(placeholders).forEach(key => {
      safe = safe.replace(key, placeholders[key]);
    });
    
    return safe;
  }
}
